import styled, { DefaultTheme } from 'styled-components';
import { Card, Typography, Collapse } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 24px;
  background: ${(props) => getThemeValue(props, 'transparent', '#f5f5f5')};
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderSection = styled.div`
  margin-bottom: 32px;
`;

export const MainTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  font-size: 28px !important;

  @media (max-width: 768px) {
    font-size: 24px !important;
  }
`;

export const ReportsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const ReportCard = styled(Card)<{ isSelected: boolean; cardColor: string }>`
  border-radius: 12px;
  border: 2px solid
    ${(props) =>
      props.isSelected
        ? props.cardColor
        : getThemeValue(props, '#30363d', '#e5e7eb')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) =>
    props.isSelected
      ? `0 8px 16px ${props.cardColor}40`
      : getThemeValue(
          props,
          '0 2px 8px rgba(0, 0, 0, 0.3)',
          '0 2px 8px rgba(0, 0, 0, 0.08)'
        )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  height: 100%;

  &:hover {
    transform: translateY(-1px);
    border-color: ${(props) => props.cardColor};
    box-shadow: ${(props) => `0 12px 24px ${props.cardColor}50`};
  }

  .ant-card-body {
    padding: 16px;
    border-radius: 12px;
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

export const IconWrapper = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  font-size: 24px;
  flex-shrink: 0;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

export const CardTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  font-size: 20px !important;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 18px !important;
  }
`;

export const CardDescription = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-size: 14px;
  display: block;
  margin-top: 8px;
`;

export const StyledCollapse = styled(Collapse)`
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e5e7eb')};
  border-radius: 12px;
  overflow: hidden;

  .ant-collapse-item {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e5e7eb')};

    &:last-child {
      border-bottom: none;
    }
  }

  .ant-collapse-header {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    padding: 20px 24px !important;
    font-weight: 600;
    font-size: 16px;

    @media (max-width: 768px) {
      padding: 16px 20px !important;
      font-size: 14px;
    }
  }

  .ant-collapse-content {
    background: ${(props) => getThemeValue(props, '#0d1117', '#f9fafb')};
    border-top: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e5e7eb')};
  }

  .ant-collapse-content-box {
    padding: 24px !important;

    @media (max-width: 768px) {
      padding: 20px !important;
    }
  }
`;

export const SectionContent = styled.div`
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
`;

export const SectionTitle = styled(Typography.Title)`
  margin: 0 0 16px 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 600 !important;
  font-size: 18px !important;

  @media (max-width: 768px) {
    font-size: 16px !important;
  }
`;

export const SectionText = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-size: 14px;
  line-height: 1.6;
  display: block;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
`;

export const EmptyStateText = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-size: 14px;
  display: block;
`;
